import * as React from 'react';
import { Input, Popover, Spin } from 'antd';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { debounce } from '../../utils/PerformanceUtils';
import { ReactNode } from 'react';
import { getEntityById, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import { EmptyState } from '../empty_state/EmptyState';
import Ellipsis from 'ant-design-pro/lib/Ellipsis';
import { MAX_OW_RADIO_TEXT_LENGTH } from '../../utils/DataConstants';
import posthog from 'posthog-js';
import { cn } from '../../utils/ClassNamesUtils';

require('./VisualRadioInput.less');

export const OWRadioDisplayWithIcons = ({
	sortedVisibleRecords,
	valueAccessor,
	fieldName,
	value,
	searchText,
	handleSameValueClicked,
	handleValueChange,
	renderRecord,
	renderIcon,
	dropdownRecords,
	canShowList = false,
	getDescription,
}) => {
	return (
		<div className="radio-container radio-container--center radio-container--with-icons">
			{sortedVisibleRecords.map((option) => {
				const renderedRecord = renderRecord(option, dropdownRecords, searchText);
				const renderedIcon = renderIcon ? renderIcon(option) : null;
				const hasIcon = renderedIcon || option.icon;
				const textContent =
					typeof renderedRecord === 'string' && canShowList ? (
						<Ellipsis tooltip={true} length={MAX_OW_RADIO_TEXT_LENGTH}>
							{renderedRecord}
						</Ellipsis>
					) : (
						renderedRecord
					);
				return (
					<Popover
						key={valueAccessor(option)}
						content={getDescription ? getDescription(option, searchText) : null}
					>
						<div key={valueAccessor(option)} className={option.disabled ? `disabled` : ''}>
							<div
								className={`radio-outer ${value == valueAccessor(option) ? 'checked' : ''} ${
									option.disabled ? 'disabled' : ''
								} ${hasIcon ? 'state-in-box' : ''}`}
							>
								<input
									key={valueAccessor(option)}
									type="radio"
									name={fieldName}
									id={valueAccessor(option)}
									required={option.required}
									disabled={option.disabled}
									onClick={handleSameValueClicked}
									checked={value == valueAccessor(option)}
									onChange={handleValueChange}
									value={valueAccessor(option)}
								/>
								<label htmlFor={valueAccessor(option)}>
									<span data-pop-class="radio-pop abc">
										<div>
											<div className="state-in-box-inner [&_span]:line-clamp-6">
												{hasIcon && (
													<div className={renderIcon ? 'state-map state-map--logo' : 'state-map'}>
														{renderedIcon ? (
															renderedIcon
														) : (
															<i className={`icons8-font ${option.icon}`} />
														)}
													</div>
												)}
												{textContent}
											</div>
										</div>
									</span>
								</label>
								<br />
								<div className="radio-box">
									<div className="radio-round"></div>
								</div>
							</div>
							<span className="feature-list"></span>
						</div>
					</Popover>
				);
			})}
		</div>
	);
};

export const OWRadioDisplayWithoutIcon = ({
	sortedVisibleRecords,
	valueAccessor,
	fieldName,
	value,
	searchText,
	handleSameValueClicked,
	handleValueChange,
	renderRecord,
	dropdownRecords,
	canShowList = false,
	getDescription,
}) => {
	return (
		<div className="radio-container radio-container--center">
			{sortedVisibleRecords.map((option, idx) => {
				const renderedRecord = renderRecord(option, dropdownRecords, searchText);
				return (
					<Popover
						key={valueAccessor(option)}
						content={getDescription ? getDescription(option, searchText) : null}
					>
						<div key={valueAccessor(option)} className={option.disabled ? `disabled` : ''}>
							<div
								className={`radio-outer ${value == valueAccessor(option) ? 'checked' : ''} ${
									option.disabled ? 'disabled' : ''
								}`}
							>
								<input
									key={valueAccessor(option)}
									type="radio"
									name={fieldName}
									id={valueAccessor(option)}
									required={option.required}
									disabled={option.disabled}
									onClick={handleSameValueClicked}
									checked={value == valueAccessor(option)}
									onChange={handleValueChange}
									value={valueAccessor(option)}
								/>
								<label htmlFor={valueAccessor(option)}>
									<span data-pop-class="radio-pop">
										{typeof renderedRecord === 'string' && canShowList ? (
											<Ellipsis tooltip={true} length={MAX_OW_RADIO_TEXT_LENGTH}>
												{renderedRecord}
											</Ellipsis>
										) : (
											renderedRecord
										)}
									</span>
								</label>
								<br />
								<div className="radio-box">
									<div className="radio-round"></div>
								</div>
							</div>
							<span className="feature-list"></span>
						</div>
					</Popover>
				);
			})}
		</div>
	);
};

interface OWAsyncRadioProps {
	stateSlice: any;
	targetCollectionName: string;
	placeholder?: string;
	valueAccessor?: any;
	additionalFilters?: any;
	outerStyle?: any;
	optionStyle?: any;
	initialPagination?: any;
	size?: 'default' | 'large' | 'small';
	style?: any;
	mode?: 'default' | 'multiple' | 'tags' | 'combobox';
	fieldName: any;
	value?: any;
	onChange?: any;
	onClick?: any;
	loading?: boolean;
	disabled?: boolean;
	emptyWhenDisabled?: boolean;
	sortBy?: any;
	keepFetchedOrder?: boolean;
	autoPickFirstOption?: boolean;
	scrollableMaxHeight?: string;

	fetchMultiple(ids: string[] | number[], targetCollectionName?: string): void;

	renderRecord(data: any, index?: any, searchText?: any): ReactNode;
	renderIcon?(data: any): ReactNode;
	getDescription?(data: any, index?: any, searchText?: any): ReactNode;

	fetchData(
		searchText: string,
		targetCollectionName: string,
		pagination?: any,
		sorting?: any,
		filters?: any,
		addToTargetCollection?: boolean
	): any;

	orderRecords?(data: any): any;
}

const defaultPagination = { current: 1, pageSize: 100 };
const LOAD_MORE_SCROLL_THRESHOLD_PX = 8;

class OWAsyncRadio extends React.Component<OWAsyncRadioProps, any> {
	constructor(props) {
		super(props);
		const value = props.value;
		this.state = {
			value: value,
			loading: true,
		};
	}

	componentDidUpdate(prevProps) {
		// Should be a controlled component.
		if ('value' in this.props && this.props.value !== prevProps.value) {
			this.setState({ value: this.props.value });
		}
	}

	getTargetCollection = () => {
		const { stateSlice, targetCollectionName } = this.props;
		return targetCollectionName
			? nullSafeGet(targetCollectionName, stateSlice)
			: stateSlice;
	};

	getCurrentPagination = () => {
		const slicePagination = nullSafeGet('pagination', this.getTargetCollection());
		return slicePagination && slicePagination.current && slicePagination.pageSize
			? slicePagination
			: this.props.initialPagination || defaultPagination;
	};

	hasMoreRecords = () => {
		const targetCollection = this.getTargetCollection();
		const pagination = this.getCurrentPagination();
		const pagedRecordsCount = pagination.current * pagination.pageSize;
		const loadedRecordsCount = nullSafeGet('recordsIds.length', targetCollection) || 0;
		if (typeof targetCollection.count === 'number') {
			return loadedRecordsCount < targetCollection.count && pagedRecordsCount < targetCollection.count;
		}
		return loadedRecordsCount >= pagedRecordsCount;
	};

	handleLoadMore = debounce(() => {
		const { loading } = this.props;
		if (loading || nullSafeGet('fetching', this.getTargetCollection())) {
			return;
		}
		if (!this.hasMoreRecords()) {
			return;
		}
		const pagination = this.getCurrentPagination();
		const newPagination = { current: pagination.current + 1, pageSize: pagination.pageSize };
		this.props.fetchData(
			this.state.searchText && this.state.searchText.trim(),
			this.props.targetCollectionName,
			newPagination,
			this.props.sortBy,
			this.props.additionalFilters,
			true
		);
		this.setState({ pagination: newPagination });
	}, 500);

	handleScroll = (event) => {
		const scrollHeight = nullSafeGetOrElse('target.scrollHeight', event, 0);
		const scrollTop = nullSafeGetOrElse('target.scrollTop', event, 0);
		const offsetHeight = nullSafeGetOrElse('target.offsetHeight', event, 0);
		if (scrollHeight > 0 && scrollTop + offsetHeight >= scrollHeight - LOAD_MORE_SCROLL_THRESHOLD_PX) {
			this.handleLoadMore();
		}
	};

	componentDidMount() {
		const { value } = this.state;
		const { targetCollectionName } = this.props;
		if (value) {
			this.props.fetchMultiple(Array.isArray(value) ? value : [value]);
			this.props
				.fetchData(
					'',
					targetCollectionName,
					this.props.initialPagination || defaultPagination,
					this.props.sortBy,
					this.props.additionalFilters,
					false
				)
				.then(() => {
					this.setState({ loading: false });
				});
		} else {
			this.props
				.fetchData(
					'',
					targetCollectionName,
					this.props.initialPagination || defaultPagination,
					this.props.sortBy,
					this.props.additionalFilters,
					false
				)
				.then((data) => {
					const { autoPickFirstOption = false, valueAccessor = (el) => el && el.id } = this.props;
					if (autoPickFirstOption && data && data.length === 1) {
						this.triggerChange(valueAccessor(data[0]));
					}
					this.setState({ loading: false });
				});
		}
	}

	handleSameValueClicked = (e) => {
		const { onClick } = this.props;
		if (nullSafeGet('target.value', e) == this.state.value && onClick) {
			onClick(e.target.value);
		}
	};

	handleValueChange = (e) => {
		if (!('value' in this.props)) {
			this.setState({ value: e.target.value });
		}
		this.triggerChange(e.target.value);
	};

	triggerChange = (changedValue) => {
		// Should provide an event to pass value to Form.
		const { onChange } = this.props;
		if (onChange) {
			onChange(changedValue);
		}
	};

	handleSearchDebounced = (e) => {
		const searchText = e.target.value && e.target.value.trim();
		this.handleSearch(searchText);
	};

	handleSearch = debounce(
		(searchText) => {
			this.props.fetchData(
				searchText,
				this.props.targetCollectionName,
				this.props.initialPagination || defaultPagination,
				this.props.sortBy,
				this.props.additionalFilters,
				false
			);
			this.setState({ searchText, pagination: this.props.initialPagination || defaultPagination });
		},
		400,
		false
	);

	render() {
		const { value } = this.state;
		const {
			disabled,
			keepFetchedOrder,
			fieldName,
			emptyWhenDisabled,
			valueAccessor = (el) => el && el.id,
			stateSlice,
			targetCollectionName,
			mode,
			placeholder,
			size,
			style,
			renderRecord,
			renderIcon,
			outerStyle = {},
			optionStyle = { height: 228 },
			orderRecords,
			getDescription,
			scrollableMaxHeight,
		} = this.props;
		const selectedVals = Array.isArray(value) ? value : [value];
		const selectedRecords = selectedVals
			// `null` fallback keeps a cache miss falsy so the `r &&` guard below still short-circuits
			// before `valueAccessor` runs — see the same note in OWAsyncSelect.
			.map((v) => getEntityById(v, stateSlice, null))
			.filter((r) => r && valueAccessor(r));
		const dropdownRecords = getRecordsForTargetCollection(stateSlice, targetCollectionName);
		const visibleRecords = selectedRecords
			.concat(dropdownRecords.filter((r) => !selectedVals.some((val) => valueAccessor(r) == val)))
			.filter((e) => e);
		const sortedVisibleRecords = orderRecords
			? orderRecords(visibleRecords)
			: keepFetchedOrder
			? visibleRecords
			: visibleRecords.sort((a, b) => (a.name > b.name ? 1 : -1));
		const { loading } = this.state;
		const fetching = nullSafeGet('fetching', this.getTargetCollection());
		const hasMore = this.hasMoreRecords();
		return (
			<div>
				{sortedVisibleRecords.length <= 8 &&
				(!this.state.searchText || this.state.searchText.length === 0) ? null : (
					<div style={{ paddingBottom: 16, textAlign: 'center' }}>
						<Input.Search
							size="large"
							style={{ width: 320 }}
							onChange={this.handleSearchDebounced}
						/>
					</div>
				)}
				{loading ? (
					<PageLoadingPlaceholder />
				) : (
					<div
						className={cn('relative', scrollableMaxHeight || 'max-h-96', 'overflow-y-auto')}
						onScroll={this.handleScroll}
					>
						{sortedVisibleRecords.some((option) => option.icon) || renderIcon ? (
							//If the option has an icon or a custom renderIcon
							<>
								{((sortedVisibleRecords || []).length < 1 && (
									<EmptyState height={120} headline={'No data found'} />
								)) ||
									null}
								<OWRadioDisplayWithIcons
									getDescription={getDescription}
									sortedVisibleRecords={sortedVisibleRecords}
									valueAccessor={valueAccessor}
									fieldName={fieldName}
									value={this.state.value}
									searchText={this.state.searchText}
									handleSameValueClicked={this.handleSameValueClicked}
									handleValueChange={this.handleValueChange}
									renderRecord={renderRecord}
									renderIcon={renderIcon}
									dropdownRecords={dropdownRecords}
								/>
							</>
						) : (
							<>
								{((sortedVisibleRecords || []).length < 1 && (
									<EmptyState height={120} headline={'No data found'} body={null} />
								)) ||
									null}
								<OWRadioDisplayWithoutIcon
									getDescription={getDescription}
									sortedVisibleRecords={sortedVisibleRecords}
									valueAccessor={valueAccessor}
									fieldName={fieldName}
									value={this.state.value}
									searchText={this.state.searchText}
									handleSameValueClicked={this.handleSameValueClicked}
									handleValueChange={this.handleValueChange}
									renderRecord={renderRecord}
									dropdownRecords={dropdownRecords}
								/>
							</>
						)}
						{fetching && hasMore && (
							<div className="sticky bottom-0 left-0 right-0 flex justify-center border-t border-grey-4 bg-white py-2">
								<Spin size="small" />
							</div>
						)}
					</div>
				)}
			</div>
		);
	}
}

export default OWAsyncRadio;
